import * as React from 'react';
import { connect } from 'react-redux';
import { Container, Modal, ModalBody, ModalFooter } from 'reactstrap';
import { bindActionCreators, Dispatch } from 'redux';

import * as PartnerExportActions from '../../actions/PartnerExport';

import * as PartnersApi from '../../api/partners';
import Localization from '../../config/localization';
import { ONE_DAY } from '../../constants/constants';

import BackButton from '../../components/BackButton';
import CustomFile from '../../components/File';
import Loader from '../../components/Loader';
import CloseSvg from '../../components/Svg/close';
import DownloadSvg from '../../components/Svg/download';
import StopSvg from '../../components/Svg/stop';

import Notifications from '../../lib/notifications';
import { DownloadFile, StrToDateStr, ToNumber } from '../../lib/utils';

import { EXPORT_PARTNER_GET_PARTNER_INTERVAL } from '../../constants/export';

import './ExportPartner.scss';
import { isExportCompleted, isExportStarted } from '../../lib/export';

type IProps = ExportPartner.IProps & ReturnType<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

class ExportPartner extends React.Component<IProps, ExportPartner.IState> {
  state: ExportPartner.IState = {
    countLoader: false,
    loading: false,
    loadingMessage: '',

    exports: [],

    countPartner: 0,
    eeCount: 0,
    gnCount: 0,

    interval: undefined,

    exportCanDownload: false,
    exportIsRunning: false
  };

  componentDidMount() {
    return Promise.all([this._getCount(), this._getStatus()]);
  }

  componentWillUnmount() {
    if (this.state.interval) {
      clearInterval(this.state.interval);
    }
  }

  renderDownloadButton = (dbExport: Partner.IExportPartner) => {
    return (
      <div className="d-inline-flex my-auto c-pointer ml-auto mr-5 h-100 eon-red"
           onClick={() => this._downloadExport(dbExport)}>
        <DownloadSvg className="mx-1 mr-2 my-auto" />
        <p className="m-0">{Localization.partnerExportDownloadButton}</p>
      </div>
    );
  };

  renderCancelButton = (dbExport: Partner.IExportPartner) => {
    return (
      <div className="d-inline-flex my-auto c-pointer ml-auto mr-5 h-100" onClick={() => this._cancelExport(dbExport)}>
        <CloseSvg className="mx-1 mr-2 my-auto" />
        <p className="m-0">{Localization.partnerExportCancelButton}</p>
      </div>
    );
  };

  renderProgressSubMessage = (dbExport: Partner.IExportPartner) => {
    if (this._isReady(dbExport) || !dbExport) {
      return undefined;
    }

    return (
      <p className="eon-caption eon-middle-grey m-0">
        {Localization.formatString(Localization.partnerExportProcessingSubMessage, ToNumber(dbExport.progress), ToNumber(dbExport.total))}
      </p>
    );
  };

  renderExpireDate = (dbExport: Partner.IExportPartner) => {
    if (!this._isReady(dbExport) || !dbExport.expireDate) {
      return undefined;
    }

    return (
      <p className="eon-caption eon-middle-grey m-auto">
        {Localization.formatString(Localization.partnerExportExpireMessage, StrToDateStr(dbExport.expireDate))}
      </p>
    );
  };

  renderExports = () => {
    return this.state.exports.map((dbExport, index) => (
      <CustomFile
        containerClass={index ? 'mt-4' : ''}
        key={dbExport.partnerExportId || index}
        fileName={this._getFileName(dbExport)}
        fileSize={dbExport.fileSize}
        progressMessage={!this._isReady(dbExport) ? Localization.partnerExportProcessingMessage : ''}
        expireDate={this.renderExpireDate(dbExport)}
        progressColor={'eon-turquoise'}
        progressValue={this._getProgress(dbExport)}
        isReady={this._isReady(dbExport)}
        downloadButton={this.renderDownloadButton(dbExport)}
        cancelButton={this.renderCancelButton(dbExport)}
        progressSubMessage={this.renderProgressSubMessage(dbExport)}
      />
    ));
  };

  renderCountContainer = (str = '', count = 0) => {
    return (
      <div className="d-flex mt-3">
        <StopSvg className="m-2 mr-3" />
        <div className="d-flex my-auto w-100">
          <p className="eon-title eon-middle-grey text-nowrap m-0">{Localization.formatString(str, count)}</p>
        </div>
      </div>
    );
  };

  renderModalExportRunning = () => {
    return (
      <Modal centered isOpen={this.state.exportIsRunning} toggle={this._closeExportIsRunning}>
        <ModalBody className="px-4 pt-4">
          <h4 className="eon-title eon-darkgrey">{Localization.partnerExportExportIsRunning}</h4>
        </ModalBody>
        <ModalFooter className="my-4 border-0 justify-content-start">
          <button className="eon-button bg-eon-red eon-white mx-5" onClick={this._closeExportIsRunning}>
            <span className="px-3">{Localization.partnerExportExportIsRunningButton}</span>
          </button>
        </ModalFooter>
      </Modal>
    );
  };

  renderModalCanDownload = () => {
    return (
      <Modal centered isOpen={this.state.exportCanDownload} toggle={this._closeExportExist}>
        <ModalBody className="px-4 pt-4">
          <h4 className="eon-title eon-darkgrey">{Localization.partnerExportExportCanDownload}</h4>
        </ModalBody>
        <ModalFooter className="my-4 border-0 justify-content-start">
          <button className="eon-button bg-eon-lightgrey eon-white mx-5" onClick={this._closeExportExist}>
            <span className="px-3">{Localization.partnerExportExportCanDownloadButtonNo}</span>
          </button>
          <button className="eon-button bg-eon-red eon-white mx-5" onClick={this._doExport}>
            <span className="px-3">{Localization.partnerExportExportCanDownloadButtonYes}</span>
          </button>
        </ModalFooter>
      </Modal>
    );
  };

  render() {
    return (
      <Container className="export-partner px-5 py-4 my-4">
        <BackButton />

        <Loader loading={this.state.loading} loadingMessage={this.state.loadingMessage} />

        {this.renderModalExportRunning()}
        {this.renderModalCanDownload()}

        <div className="d-flex mt-5">
          <div className="w-50">
            <h4 className="eon-title eon-red force-wrap w-50">{Localization.partnerExportTitle}</h4>
            <p className="mt-3 mb-5 eon-title eon-middle-grey wrap-pre-line">{Localization.partnerExportSubtitle}</p>

            {(!this.state.countLoader && (
              <React.Fragment>
                {this._countGN()}
                {this._countEE()}
              </React.Fragment>
            )) || <p className="eon-title eon-middle-grey text-nowrap m-0">{Localization.partnerExportCountLoading}</p>}

            <button className="mt-5 eon-button bg-eon-red eon-white" onClick={this._doExport}>
              <span className="px-4">{Localization.partnerExportButton}</span>
            </button>
          </div>
          <div className="exports-container w-50">{this.renderExports()}</div>
        </div>
      </Container>
    );
  }

  _getInterval = (state = this.state) => {
    if (
      state.exports &&
      state.exports.length &&
      !this.state.interval &&
      state.exports.find(dbExport => isExportStarted(dbExport.status))
    ) {
      return setInterval(() => this._getStatus(), EXPORT_PARTNER_GET_PARTNER_INTERVAL);
    }
    return undefined;
  };

  _getStatus = async () => {
    try {
      if (!this.state.interval) {
        await this.setState({ loading: true });
      }

      const response = await PartnersApi.GetExportStatus();

      const data = (response.data || []).filter(
        dbExport => isExportCompleted(dbExport.status) || isExportStarted(dbExport.status)
      );

      const newState = { exports: data, loading: false } as ExportPartner.IState;

      const interval = this._getInterval(newState);

      if (interval) {
        newState.interval = interval;
      }

      return this.setState(newState);
    } catch (error) {
      Notifications.error(error && error.message);

      return this.setState({ loading: false });
    }
  };

  _getCount = async (skipStorage = false) => {
    try {
      if (
        !skipStorage &&
        this.props.PartnerExport &&
        (this.props.PartnerExport.eeCount || this.props.PartnerExport.gnCount) &&
        ToNumber(this.props.PartnerExport.expireAt) > Date.now()
      ) {
        return this.setState({ gnCount: this.props.PartnerExport.gnCount, eeCount: this.props.PartnerExport.eeCount });
      }

      await this.setState({ countLoader: true });

      const response = await PartnersApi.GetExportCount();

      this.props.SetPartnerExport({
        eeCount: response.data.eeCount,
        expireAt: Date.now() + ONE_DAY,
        gnCount: response.data.gnCount
      });

      return this.setState({
        countLoader: false,
        countPartner: response.data.countPartner,
        eeCount: response.data.eeCount,
        gnCount: response.data.gnCount
      });
    } catch (error) {
      Notifications.error(error && error.message);

      return this.setState({ countLoader: false });
    }
  };

  _doExport = async (event: React.MouseEvent<HTMLButtonElement>) => {
    try {
      event.preventDefault();

      if (this.state.loading) {
        return undefined;
      }

      await this._getStatus();

      await this.setState({ loading: true });

      if (this._exportIsRunning()) {
        return this.setState({ exportIsRunning: true, loading: false });
      } else if (this._exportCanDownload() && !this.state.exportCanDownload) {
        return this.setState({ exportCanDownload: true, loading: false });
      }

      await this.setState({ exportCanDownload: false, exportIsRunning: false });

      this._getCount(true);

      const response = await PartnersApi.ExportPartner();

      const state = {
        loading: false,
        exports: (this.state.exports || []).concat(response.data)
      } as ExportPartner.IState;

      const interval = this._getInterval(state);

      if (interval) {
        state.interval = interval;
      }

      return this.setState(state);
    } catch (error) {
      Notifications.error(error && error.message);

      return this.setState({ loading: false });
    }
  };

  _cancelExport = async (dbExport: Partner.IExportPartner) => {
    try {
      if (!dbExport) {
        return undefined;
      }

      await this.setState({ loading: true });

      await PartnersApi.CancelExport(dbExport.partnerExportId);

      const exportsData = this.state.exports || [];

      exportsData.splice(exportsData.findIndex(data => data.partnerExportId === dbExport.partnerExportId), 1);

      return this.setState({ loading: false, exports: exportsData });
    } catch (error) {
      Notifications.error(error && error.message);

      return this.setState({ loading: false });
    }
  };

  _downloadExport = async (dbExport: Partner.IExportPartner) => {
    try {
      if (!dbExport) {
        return undefined;
      }

      await this.setState({ loading: true });

      const response = await PartnersApi.GerExportFile(dbExport.partnerExportId);

      const fileName = this._getFileName(dbExport);

      await DownloadFile(response.data, `${fileName}`);

      return this.setState({ loading: false });
    } catch (error) {
      Notifications.error(error && error.message);

      return this.setState({ loading: false });
    }
  };

  _closeExportIsRunning = () => {
    return this.setState({ exportIsRunning: false });
  };

  _closeExportExist = () => {
    return this.setState({ exportCanDownload: false });
  };

  _exportIsRunning = () => {
    return !!(
      this.state.exports &&
      this.state.exports.length &&
      this.state.exports.find(dbExport => isExportStarted(dbExport.status))
    );
  };

  _exportCanDownload = () => {
    return !!(
      this.state.exports &&
      this.state.exports.length &&
      this.state.exports.find(dbExport => isExportCompleted(dbExport.status))
    );
  };

  _countGN = () => {
    if (!this.state.gnCount) {
      return null;
    }

    return this.renderCountContainer(Localization.partnerExportCountGn, this.state.gnCount);
  };

  _countEE = () => {
    if (!this.state.gnCount) {
      return null;
    }

    return this.renderCountContainer(Localization.partnerExportCountEe, this.state.eeCount);
  };

  _getProgress = (dbExport: Partner.IExportPartner) => {
    if (!dbExport) {
      return 0;
    }
    return Math.round((ToNumber(dbExport.progress) * 100) / ToNumber(dbExport.total));
  };

  _isReady = (dbExport: Partner.IExportPartner) => {
    if (!dbExport) {
      return false;
    }
    return !!(dbExport.total && dbExport.progress && isExportCompleted(dbExport.status));
  };

  _getFileName = (dbExport: Partner.IExportPartner) => {
    if (!dbExport) {
      return '';
    }
    return Localization.formatString(Localization.partnerExportFileName, StrToDateStr(dbExport.createdDate));
  };
}

const mapStateToProps = (state: General.Store) => ({ PartnerExport: state.PartnerExport });

const mapDispatchToProps = (dispatch: Dispatch) => ({ ...bindActionCreators({ ...PartnerExportActions }, dispatch) });

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ExportPartner);
